import { useRef, useState, type FormEvent } from 'react';
import {
  MARKETERS_APPLY_ID,
  marketersAgency as content,
  marketersCardDisclaimer,
} from '~/content/marketers';
import { Section, SectionHeading } from '~/components/layout';
import { BusinessFormSuccess, Button, SmartImage } from '~/components/ui';
import {
  AGENCY_APPLICATION_FIELDS,
  AGENCY_APPLICATION_MAX_LENGTH,
  validateAgencyApplication,
  type AgencyApplication,
  type AgencyApplicationField,
} from '~/lib/agency-application-validation';
import { cn } from '~/lib/cn';

type SubmissionState =
  | { status: 'idle' | 'submitting' | 'success'; message?: never }
  | { status: 'error'; message: string };

type FieldErrors = Partial<Record<AgencyApplicationField, string>>;

function readForm(form: FormData): AgencyApplication {
  return Object.fromEntries(
    AGENCY_APPLICATION_FIELDS.map((field) => [field, String(form.get(field) ?? '').trim()]),
  ) as AgencyApplication;
}

/**
 * "Are you an agency owner?" Infinite card application (Figma 1272:49041 /
 * mobile 1111:18665). Desktop: the form panel floats over the full-bleed card
 * image. Mobile: the image stacks above the form. Submits to
 * /api/agency-application.
 */
export function MarketersAgencyApplication() {
  const [submission, setSubmission] = useState<SubmissionState>({ status: 'idle' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // performance.now() is monotonic, so the elapsed time sent to the server is
  // unaffected by a wrong device clock.
  const formStartedAt = useRef(performance.now());

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const values = readForm(form);
    const errors = validateAgencyApplication(values);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setSubmission({ status: 'error', message: 'Check the highlighted fields and try again.' });
      const firstInvalid = content.fields.find((field) => errors[field.name]);
      if (firstInvalid) {
        (formElement.elements.namedItem(firstInvalid.name) as HTMLElement | null)?.focus();
      }
      return;
    }

    setFieldErrors({});
    setSubmission({ status: 'submitting' });
    try {
      const response = await fetch('/api/agency-application', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...values,
          fax: form.get('fax'),
          elapsedMs: Math.round(performance.now() - formStartedAt.current),
        }),
      });
      const result = (await response.json().catch(() => null)) as {
        ok?: boolean;
        code?: string;
      } | null;

      if (!response.ok || !result?.ok) {
        setSubmission({
          status: 'error',
          message:
            result?.code === 'invalid_submission'
              ? 'Check the highlighted fields and try again.'
              : result?.code === 'rate_limited'
                ? 'Too many attempts. Please wait 15 minutes and try again.'
                : 'We could not submit your application right now. Please try again.',
        });
        return;
      }

      setSubmission({ status: 'success' });
    } catch {
      setSubmission({
        status: 'error',
        message: 'We could not submit your application right now. Please try again.',
      });
    }
  };

  const onInput = (event: FormEvent<HTMLFormElement>) => {
    const field = (event.target as HTMLInputElement).name as AgencyApplicationField;
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    if (submission.status === 'error') setSubmission({ status: 'idle' });
  };

  return (
    <Section className="pt-20 md:pt-[200px]">
      <div id={MARKETERS_APPLY_ID} className="relative scroll-mt-24 lg:min-h-[762px]">
        <div className="relative aspect-[353/482] w-full overflow-hidden md:aspect-[1400/762] lg:absolute lg:inset-0 lg:aspect-auto">
          <SmartImage image={content.image} className="size-full" />
          <span
            aria-hidden
            className="absolute inset-0 hidden bg-[linear-gradient(95.74deg,rgba(0,0,0,0.2)_23.81%,rgba(255,255,255,0)_97.13%)] lg:block"
          />
        </div>

        <div className="relative mt-[60px] lg:mt-0 lg:ms-5 lg:pt-5 lg:pb-5">
          <div className="lg:min-h-[722px] lg:w-[511px] lg:bg-white lg:p-5">
            <SectionHeading title={content.title} className="lg:max-w-[450px]" />
            {submission.status === 'success' ? (
              <BusinessFormSuccess title={content.successTitle}>
                {content.successBody}
              </BusinessFormSuccess>
            ) : (
              <>
                <p className="mt-[35px] text-[15px] leading-[1.32] tracking-[-0.03px] text-grey-900 md:mt-5 md:text-body md:text-grey-600">
                  {content.body}
                </p>
                <form className="mt-10" onSubmit={onSubmit} onInput={onInput} noValidate>
                  <div className="flex flex-col gap-5 md:gap-[26px]">
                    {content.fields.map((field) => {
                      const error = fieldErrors[field.name];
                      const id = `agency-application-${field.name}`;
                      return (
                        <div key={field.name} className="flex flex-col">
                          <input
                            id={id}
                            name={field.name}
                            type={field.type}
                            placeholder={field.placeholder}
                            aria-label={field.placeholder}
                            autoComplete={field.autoComplete}
                            inputMode={field.type === 'tel' ? 'tel' : undefined}
                            maxLength={AGENCY_APPLICATION_MAX_LENGTH[field.name]}
                            required
                            aria-invalid={Boolean(error)}
                            aria-describedby={error ? `${id}-error` : undefined}
                            className={cn(
                              'h-[50px] w-full rounded-[10px] border bg-[#f6f6f6] ps-5 pe-[14px] text-[16px] font-medium tracking-[0.192px] text-ink outline-none transition-[border-color,box-shadow] placeholder:text-grey-400 focus:border-red-500 focus:ring-3 focus:ring-red-500/10',
                              error ? 'border-red-500' : 'border-transparent',
                            )}
                          />
                          {error && (
                            <span
                              id={`${id}-error`}
                              className="mt-2 text-[13px] leading-[1.45] text-red-500">
                              {error}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <input
                    type="text"
                    name="fax"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute -left-[10000px] h-px w-px overflow-hidden"
                  />
                  {submission.status === 'error' && (
                    <p role="alert" className="mt-6 text-body-s text-red-500">
                      {submission.message}
                    </p>
                  )}
                  <Button
                    type="submit"
                    disabled={submission.status === 'submitting'}
                    className="mt-[35px] max-md:!text-[14px] max-md:!tracking-[0.168px] md:mt-10">
                    {content.cta}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
      <p className="mt-20 text-[15px] leading-[1.32] tracking-[0.27px] text-grey-700 lg:mt-10 lg:text-black">
        {marketersCardDisclaimer}
      </p>
    </Section>
  );
}
